/*----------------------------------
- DOMAINS CARD VISUALS (decorative only)
- The supplied sample domains keep their reference icon and color. Remaining
- domains use a stable hash of the domain string.
- Presentation mechanics only: this encodes no demand, quality, or hotness signal.
----------------------------------*/

const CARD_ICON_COUNT = 12;

const CARD_ICON_COLORS = [
    '#f97316',
    '#3b82f6',
    '#ef4444',
    '#ec4899',
    '#a855f7',
    '#8b5cf6',
    '#eab308',
    '#22c55e',
    '#14b8a6',
] as const;

const REFERENCE_CARD_VISUALS: Readonly<Record<string, CardVisual>> = {
    'summit.co': { color: '#f97316', iconIndex: 3 },
    'atlas.ai': { color: '#22c55e', iconIndex: 11 },
    'nimbus.io': { color: '#3b82f6', iconIndex: 0 },
    'radius.co': { color: '#eab308', iconIndex: 7 },
    'orbit.app': { color: '#14b8a6', iconIndex: 3 },
    'lumen.dev': { color: '#a855f7', iconIndex: 7 },
    'vertex.ai': { color: '#ec4899', iconIndex: 2 },
    'harbor.co': { color: '#f97316', iconIndex: 7 },
};

const stableHash = (value: string): number => {
    let hash = 0;
    for (let index = 0; index < value.length; index += 1) {
        hash = (hash * 31 + value.charCodeAt(index)) >>> 0;
    }
    return hash;
};

export type CardVisual = {
    color: (typeof CARD_ICON_COLORS)[number];
    iconIndex: number;
};

export const pickCardVisual = (domain: string): CardVisual => {
    const referenceVisual = REFERENCE_CARD_VISUALS[domain.trim().toLowerCase()];
    if (referenceVisual) return referenceVisual;

    const hash = stableHash(domain);
    return {
        color: CARD_ICON_COLORS[(hash >>> 4) % CARD_ICON_COLORS.length],
        iconIndex: hash % CARD_ICON_COUNT,
    };
};

/** Warm status chip tones (bg/text) by normalized status. */
export const CARD_STATUS_TONE: Record<string, { bg: string; color: string }> = {
    available: { bg: '#EAF6EF', color: '#2D7A55' },
    premium: { bg: '#EFEBFA', color: '#6D5BD0' },
    resell: { bg: '#E8F0FB', color: '#315FA8' },
    auction: { bg: '#FBF4E2', color: '#A86A12' },
    // Neutral, and deliberately not the green `available` tone: an unchecked name
    // must not be dressed as a name confirmed free to register. Mapped explicitly
    // rather than by changing the fall-through below, which still has to read as
    // available for the statuses this catalog does not tone (expiring, expired, sold).
    unknown: { bg: '#F1F0EE', color: '#6B6864' },
};

const UNCHECKED_STATUS_LABEL = 'Not checked';

export const resolveCardStatus = (status?: string | null): { label: string; tone: { bg: string; color: string } } => {
    const normalized = (status || 'available').trim().replaceAll('_', ' ').toLowerCase();
    const tone = CARD_STATUS_TONE[normalized] || CARD_STATUS_TONE.available;
    const label = normalized === 'unknown'
        ? UNCHECKED_STATUS_LABEL
        : normalized.charAt(0).toUpperCase() + normalized.slice(1);
    return { label, tone };
};
